import {
  ArrowRight,
  Bell,
  Bot,
  CheckCircle2,
  ChevronRight,
  Clock,
  Database,
  ExternalLink,
  LayoutGrid,
  ListTodo,
  MessageSquare,
  Play,
  Zap,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { featureFlags } from "@/config/featureFlags";
import type { TeamHubQuickActionSummary, TeamHub as TeamHubType } from "@/types";
import { AppIconImage } from "./AppIconImage";
import { getNotificationStyle, getPriorityDot, resolveIcon, timeAgo } from "./utils";

export function HubTab({
  // NOSONAR -- hub dashboard component with conditional rendering for multiple sections
  hub,
  navigate,
  firstName,
  dismissedNotifications,
  onDismissNotification,
  onManageTasks,
  onViewAllConversations,
}: Readonly<{
  hub: TeamHubType;
  navigate: ReturnType<typeof useNavigate>;
  firstName: string;
  dismissedNotifications: Set<string>;
  onDismissNotification: (id: string) => void;
  onManageTasks?: () => void;
  onViewAllConversations?: () => void;
}>) {
  const [expandedAction, setExpandedAction] = useState<string | null>(null);
  const [actionInput, setActionInput] = useState("");

  const notifications = hub.notifications ?? [];
  const activeNotifications = notifications.filter((n) => !dismissedNotifications.has(n.uuid));
  const unreadCount = activeNotifications.filter((n) => !n.readAt).length;

  const quickActions = (hub.quickActions ?? []).filter((a) => a.isEnabled);
  const apps = hub.apps ?? [];
  const taskTemplates = (hub.taskTemplates ?? []).filter((t) => t.isEnabled);

  const handleQuickAction = (action: TeamHubQuickActionSummary) => {
    // NOSONAR -- action routing with multiple action types
    if (action.targetRoute) {
      navigate(action.targetRoute);
      return;
    }
    if (action.actionType === "agent") {
      handleAgentAction(action);
      return;
    }
    if (action.actionType === "app" && action.appUuid) {
      navigate(`/apps/${action.appUuid}`);
      return;
    }
    if (action.actionType === "flow") {
      handleFlowAction(action);
      return;
    }
  };

  const handleAgentAction = (action: TeamHubQuickActionSummary) => {
    if (action.autoSubmit && action.agentUuid && action.hintText) {
      navigate(`/chat?prefill=${encodeURIComponent(action.hintText)}&agent=${action.agentUuid}&autosubmit=1`);
    } else {
      toggleExpandedAction(action.uuid);
    }
  };

  const handleFlowAction = (action: TeamHubQuickActionSummary) => {
    if (action.autoSubmit && action.flowUuid) {
      navigate(`/flows/${action.flowUuid}/run?autostart=1`);
    } else {
      toggleExpandedAction(action.uuid);
    }
  };

  const toggleExpandedAction = (uuid: string) => {
    setExpandedAction(expandedAction === uuid ? null : uuid);
    setActionInput("");
  };

  const submitExpandedAction = (action: TeamHubQuickActionSummary, input: string) => {
    if (!input.trim()) return;
    if (action.actionType === "flow" && action.flowUuid) {
      navigate(
        `/flows/${action.flowUuid}/run?input=${encodeURIComponent(input)}${action.autoSubmit ? "&autostart=1" : ""}`,
      );
    } else {
      const agentParam = action.agentUuid ? `&agent=${action.agentUuid}` : "";
      navigate(`/chat?prefill=${encodeURIComponent(input)}${agentParam}${action.autoSubmit ? "&autosubmit=1" : ""}`);
    }
  };

  return (
    <div className="p-6 space-y-8 max-w-[1400px] mx-auto">
      {/* Welcome + Greeting */}
      <div className="bg-linear-to-r from-brand-primary/10 via-brand-secondary/10 to-brand-primary/5 border border-brand-primary/20 rounded-2xl p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-content-primary">Hey {firstName}, what are you working on?</h2>
            <p className="text-content-tertiary mt-1">
              {hub.name} hub &mdash; {hub.stats.agentCount} {hub.stats.agentCount === 1 ? "agent" : "agents"},{" "}
              {hub.stats.dataSourceCount} {hub.stats.dataSourceCount === 1 ? "knowledge base" : "knowledge bases"}, and{" "}
              {apps.length} {apps.length === 1 ? "app" : "apps"} ready to help.
            </p>
          </div>
          {unreadCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-brand-primary/20 rounded-full">
              <Bell className="h-4 w-4 text-brand-primary" />
              <span className="text-sm font-medium text-brand-primary">{unreadCount} new</span>
            </div>
          )}
        </div>
      </div>

      {/* Main 2-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left column - Quick Actions & Tasks */}
        <div className="lg:col-span-2 space-y-8">
          {/* Quick Actions */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Zap className="h-5 w-5 text-brand-primary" />
              <h2 className="text-lg font-semibold text-content-primary">Quick Actions</h2>
            </div>
            {quickActions.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {quickActions.map((action) => {
                  const Icon = resolveIcon(action.icon);
                  const isExpanded = expandedAction === action.uuid;
                  return (
                    <div key={action.uuid} className="relative">
                      <button
                        onClick={() => handleQuickAction(action)}
                        className={`w-full bg-surface-primary border rounded-xl p-4 hover:border-brand-primary/40 transition-all text-left group ${
                          isExpanded ? "border-brand-primary/40 ring-1 ring-brand-primary/20" : "border-border-primary"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className={`h-10 w-10 rounded-lg bg-linear-to-br ${action.gradient || "from-brand-primary/20 to-brand-secondary/20"} flex items-center justify-center shrink-0`}
                          >
                            <Icon className={`h-5 w-5 ${action.color || "text-brand-primary"}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-content-primary text-sm group-hover:text-brand-primary transition-colors">
                                {action.label}
                              </h3>
                              {action.targetRoute && (
                                <ArrowRight className="h-3.5 w-3.5 text-content-quaternary group-hover:text-brand-primary transition-colors opacity-0 group-hover:opacity-100" />
                              )}
                            </div>
                            <p className="text-xs text-content-tertiary mt-0.5 line-clamp-2">{action.description}</p>
                          </div>
                        </div>
                      </button>
                      {/* Expanded input state for agent/flow actions */}
                      {isExpanded && !action.targetRoute && (
                        <div className="mt-2 bg-surface-primary border border-brand-primary/30 rounded-xl p-4 shadow-lg">
                          <div className="flex gap-2">
                            <input
                              type="text"
                              autoFocus
                              placeholder={action.hintText || "Describe what you need..."}
                              value={actionInput}
                              onChange={(e) => setActionInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  submitExpandedAction(action, actionInput);
                                } else if (e.key === "Escape") {
                                  setExpandedAction(null);
                                }
                              }}
                              className="flex-1 px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-sm text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
                            />
                            <button
                              onClick={() => submitExpandedAction(action, actionInput)}
                              disabled={!actionInput.trim()}
                              className="px-4 py-2 bg-brand-gradient text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                            >
                              <Play className="h-3.5 w-3.5" />
                              Go
                            </button>
                          </div>
                          <p className="text-xs text-content-quaternary mt-2">Press Enter to start, Esc to close</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-surface-secondary/50 rounded-xl p-6 text-center">
                <Zap className="h-8 w-8 text-content-quaternary mx-auto mb-2" />
                <p className="text-sm text-content-tertiary">No quick actions configured for this team yet.</p>
              </div>
            )}
          </section>

          {/* Reusable Team Tasks */}
          {featureFlags.autopilot && taskTemplates.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <ListTodo className="h-5 w-5 text-purple-400" />
                  <h2 className="text-lg font-semibold text-content-primary">Team Tasks</h2>
                  <span className="text-xs text-content-quaternary bg-surface-secondary px-2 py-0.5 rounded-full">
                    Reusable templates
                  </span>
                </div>
                {/* This would go to a task template management page */}
                <button
                  onClick={onManageTasks}
                  className="text-sm text-brand-primary hover:text-brand-primary/80 font-medium"
                >
                  Manage tasks
                </button>
              </div>
              <div className="space-y-3">
                {taskTemplates.map((task) => {
                  const Icon = resolveIcon(task.icon);
                  return (
                    <div
                      key={task.uuid}
                      className="bg-surface-primary border border-border-primary rounded-xl p-4 hover:border-border-secondary transition-colors group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-lg bg-surface-secondary flex items-center justify-center shrink-0">
                          <Icon className={`h-5 w-5 ${task.color || "text-brand-primary"}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-content-primary text-sm">{task.name}</h3>
                          <p className="text-xs text-content-tertiary mt-0.5">{task.description}</p>
                        </div>
                        <button
                          onClick={() => {
                            const agentParam = task.agentUuid ? `agent=${task.agentUuid}` : "";
                            const prefill = task.promptTemplate
                              ? `prefill=${encodeURIComponent(task.promptTemplate)}`
                              : "";
                            const params = [agentParam, prefill].filter(Boolean).join("&");
                            const qs = params ? `?${params}` : "";
                            navigate(`/chat${qs}`);
                          }}
                          className="px-3 py-1.5 bg-surface-secondary hover:bg-interactive-active border border-border-secondary rounded-lg text-xs font-medium text-content-primary transition-colors flex items-center gap-1.5 opacity-0 group-hover:opacity-100"
                        >
                          <Play className="h-3 w-3" />
                          Run
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Team Agents - more approachable presentation */}
          {hub.agents.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Bot className="h-5 w-5 text-brand-secondary" />
                  <h2 className="text-lg font-semibold text-content-primary">Team Agents</h2>
                </div>
                <button
                  onClick={() => navigate("/agents")}
                  className="text-sm text-brand-primary hover:text-brand-primary/80 font-medium"
                >
                  View all
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {hub.agents.map((agent) => (
                  <button
                    key={agent.uuid}
                    onClick={() => navigate(`/chat?agent=${agent.uuid}`)}
                    className="bg-surface-primary border border-border-primary rounded-xl p-4 hover:border-border-secondary transition-colors text-left group"
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `${agent.color}20` }}
                      >
                        <Bot className="h-5 w-5" style={{ color: agent.color }} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium text-content-primary text-sm truncate group-hover:text-brand-primary transition-colors">
                          {agent.name}
                        </h3>
                        {agent.description && (
                          <p className="text-xs text-content-tertiary line-clamp-2 mt-0.5">{agent.description}</p>
                        )}
                        <p className="text-xs text-content-quaternary mt-1.5">
                          {agent.usageCount} {agent.usageCount === 1 ? "use" : "uses"}
                          {agent.createdByName && ` · by ${agent.createdByName}`}
                        </p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-content-quaternary group-hover:text-brand-primary transition-colors opacity-0 group-hover:opacity-100 mt-1" />
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Right column - Notifications, Apps, Activity */}
        <div className="space-y-8">
          {/* Notifications / Action Items */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-amber-400" />
                <h2 className="text-lg font-semibold text-content-primary">Action Items</h2>
                {unreadCount > 0 && (
                  <span className="w-5 h-5 rounded-full bg-brand-primary text-white text-xs font-bold flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </div>
              <button
                onClick={() => navigate("/notifications")}
                className="text-sm text-brand-primary hover:text-brand-primary/80 font-medium"
              >
                View all
              </button>
            </div>
            <div className="space-y-3">
              {activeNotifications.slice(0, 4).map((notification) => {
                const style = getNotificationStyle(notification.type);
                const NIcon = style.icon;
                const isUnread = !notification.readAt;
                return (
                  <div
                    key={notification.uuid}
                    className={`bg-surface-primary border rounded-xl p-4 transition-colors ${
                      isUnread ? `${style.border} border-l-2` : "border-border-primary"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`h-8 w-8 rounded-lg ${style.bg} flex items-center justify-center shrink-0 mt-0.5`}
                      >
                        <NIcon className={`h-4 w-4 ${style.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2">
                          <h3 className="text-sm font-medium text-content-primary leading-tight flex-1">
                            {notification.title}
                          </h3>
                          {isUnread && (
                            <div
                              className={`w-2 h-2 rounded-full ${getPriorityDot(notification.priority)} shrink-0 mt-1.5`}
                            />
                          )}
                        </div>
                        <p className="text-xs text-content-tertiary mt-1 line-clamp-2">{notification.message}</p>
                        <div className="flex items-center justify-between mt-2.5">
                          <span className="text-[11px] text-content-quaternary flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {timeAgo(notification.createdAt)}
                          </span>
                          <div className="flex items-center gap-2">
                            {notification.actionLabel && (
                              <button
                                onClick={() => (notification.actionRoute ? navigate(notification.actionRoute) : null)}
                                className="text-xs font-medium text-brand-primary hover:text-brand-primary/80 transition-colors"
                              >
                                {notification.actionLabel}
                              </button>
                            )}
                            <button
                              onClick={() => onDismissNotification(notification.uuid)}
                              className="text-xs text-content-quaternary hover:text-content-tertiary transition-colors"
                            >
                              Dismiss
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {activeNotifications.length === 0 && (
                <div className="bg-surface-secondary/50 rounded-xl p-6 text-center">
                  <CheckCircle2 className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
                  <p className="text-sm text-content-tertiary">You're all caught up!</p>
                </div>
              )}
            </div>
          </section>

          {/* Team Apps */}
          {featureFlags.apps && (
            <section>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <LayoutGrid className="h-5 w-5 text-cyan-400" />
                  <h2 className="text-lg font-semibold text-content-primary">Team Apps</h2>
                </div>
                <button
                  onClick={() => navigate("/apps")}
                  className="text-sm text-brand-primary hover:text-brand-primary/80 font-medium"
                >
                  Browse all
                </button>
              </div>
              <div className="space-y-3">
                {apps.length > 0 ? (
                  apps.map((app) => {
                    const Icon = resolveIcon(app.icon);
                    return (
                      <button
                        key={app.uuid}
                        onClick={() => {
                          if (app.url) {
                            if (app.url.startsWith("http")) {
                              window.open(app.url, "_blank");
                            } else {
                              navigate(app.url);
                            }
                          }
                        }}
                        className="w-full bg-surface-primary border border-border-primary rounded-xl p-4 hover:border-border-secondary transition-colors text-left group"
                      >
                        <div className="flex items-center gap-3">
                          {app.iconUrl ? (
                            <AppIconImage
                              src={app.iconUrl}
                              color={app.color || "#EC622B"}
                              sizeClasses="h-10 w-10 rounded-lg"
                              iconSizeClasses="h-5 w-5"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-lg bg-linear-to-br from-mutual-orange/20 to-mutual-purple/20 flex items-center justify-center shrink-0">
                              <Icon className={`h-5 w-5 ${app.color || "text-brand-primary"}`} />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-content-primary text-sm group-hover:text-brand-primary transition-colors">
                                {app.name}
                              </h3>
                              {app.url?.startsWith("http") && (
                                <ExternalLink className="h-3 w-3 text-content-quaternary" />
                              )}
                            </div>
                            <p className="text-xs text-content-tertiary mt-0.5 truncate">{app.description}</p>
                          </div>
                          <ChevronRight className="h-4 w-4 text-content-quaternary group-hover:text-brand-primary transition-colors" />
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className="bg-surface-secondary/50 rounded-xl p-6 text-center">
                    <LayoutGrid className="h-8 w-8 text-content-quaternary mx-auto mb-2" />
                    <p className="text-sm text-content-tertiary">No apps assigned to this team yet.</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Team Data Sources */}
          {hub.dataSources.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Database className="h-5 w-5 text-emerald-400" />
                  <h2 className="text-lg font-semibold text-content-primary">Knowledge Base</h2>
                </div>
                <button
                  onClick={() => navigate("/data-sources")}
                  className="text-sm text-brand-primary hover:text-brand-primary/80 font-medium"
                >
                  View all
                </button>
              </div>
              <div className="space-y-2">
                {hub.dataSources.map((ds) => (
                  <button
                    key={ds.uuid}
                    onClick={() => navigate(`/data-sources/${ds.uuid}`)}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-surface-primary border border-border-primary rounded-lg hover:border-border-secondary transition-colors text-left group"
                  >
                    <Database className="h-4 w-4 text-emerald-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-content-primary group-hover:text-brand-primary transition-colors truncate block">
                        {ds.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-content-quaternary">{ds.fileCount} files</span>
                      <span
                        className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${(() => {
                          if (ds.status === "ready") return "bg-emerald-500/20 text-emerald-400";
                          if (ds.status === "error" || ds.status === "failed") return "bg-red-500/20 text-red-400";
                          return "bg-amber-500/20 text-amber-400";
                        })()}`}
                      >
                        {ds.status}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Recent Conversations */}
          {hub.recentChats.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-content-quaternary" />
                  <h2 className="text-lg font-semibold text-content-primary">Recent Activity</h2>
                </div>
                <button
                  onClick={onViewAllConversations}
                  className="text-sm text-brand-primary hover:text-brand-primary/80 font-medium"
                >
                  View all
                </button>
              </div>
              <div className="space-y-2">
                {hub.recentChats.slice(0, 5).map((chat) => (
                  <button
                    key={chat.uuid}
                    onClick={() => navigate(`/chat/${chat.uuid}`)}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-surface-primary border border-border-primary rounded-lg hover:border-border-secondary transition-colors text-left group"
                  >
                    <div className="h-8 w-8 rounded-lg bg-brand-primary/10 flex items-center justify-center shrink-0">
                      <MessageSquare className="h-4 w-4 text-brand-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-content-primary truncate group-hover:text-brand-primary transition-colors">
                        {chat.title || "Untitled conversation"}
                      </p>
                      <p className="text-[11px] text-content-quaternary mt-0.5">
                        {chat.userName && `${chat.userName} · `}
                        {chat.agentName && `${chat.agentName} · `}
                        {timeAgo(chat.updatedAt)}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
